import { ADMIN_OWN_SIGN_IN_NEEDS_SSO_MESSAGE, type MeMfa } from "@shared/types/mfa";
import { ApiClientError } from "@/lib/api";
import { currentCorrelationId } from "@/lib/correlation";
import { signInErrorText } from "./signInErrors";

/**
 * Admin MFA (2026-09-29): the client half of the second sign-in step.
 *
 * A 401 `MFA_REQUIRED` is, like `REAUTH_REQUIRED`, not an expired session: the person signs in
 * again (`prompt: "login"`), which makes the identity provider evaluate its Conditional Access
 * policy and ask for the second factor. Two things must survive the redirect, so they live in
 * `sessionStorage`, keyed by the account:
 *
 * - **The attempt.** If the token that comes back still proves no second factor (the tenant has
 *   no policy), the screen says so and never redirects a second time for the same request
 *   (design 4.3, "No loop").
 * - **"Do not ask again on this browser".** The choice is made before the redirect; the shell
 *   remembers the browser once `/me` says the new sign-in proved MFA by its token.
 */

const ATTEMPT_KEY = "skydata.mfa.stepUpAttempt";
const REMEMBER_KEY = "skydata.mfa.rememberBrowser";

/** How long an attempt counts as "just asked": a sign-in round trip, with room to spare. */
export const STEP_UP_WINDOW_MS = 10 * 60_000;

interface Marker {
  account: string | null;
  at: number;
}

function write(key: string, account: string | null): void {
  try {
    sessionStorage.setItem(key, JSON.stringify({ account, at: Date.now() } satisfies Marker));
  } catch {
    /* private mode: the step still works, the loop guard is lost with the page */
  }
}

function readRecent(key: string, account: string | null): boolean {
  try {
    const raw = sessionStorage.getItem(key);
    if (!raw) return false;
    const marker = JSON.parse(raw) as Partial<Marker>;
    return (
      marker.account === account &&
      typeof marker.at === "number" &&
      Date.now() - marker.at < STEP_UP_WINDOW_MS
    );
  } catch {
    return false;
  }
}

function clear(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* private mode */
  }
}

/** Written just before the provider is asked for the second step. */
export function noteStepUpAttempt(account: string | null): void {
  write(ATTEMPT_KEY, account);
}

/** This account was just sent to its provider for the second step. */
export function recentStepUpAttempt(account: string | null): boolean {
  return readRecent(ATTEMPT_KEY, account);
}

export function clearStepUpAttempt(): void {
  clear(ATTEMPT_KEY);
}

/** The person ticked "Do not ask again on this browser" before the redirect. */
export function stageRememberBrowser(account: string | null): void {
  write(REMEMBER_KEY, account);
}

/** Takes the staged choice once: true when this account staged it in the last ten minutes. */
export function takeRememberBrowser(account: string | null): boolean {
  const staged = readRecent(REMEMBER_KEY, account);
  clear(REMEMBER_KEY);
  return staged;
}

/** A refusal the second sign-in step answers: an old sign-in, or one without a second factor. */
export function isStepUpRequired(error: unknown): error is ApiClientError {
  return (
    error instanceof ApiClientError &&
    (error.code === "REAUTH_REQUIRED" || error.code === "MFA_REQUIRED")
  );
}

export function isMfaRequired(error: unknown): error is ApiClientError {
  return error instanceof ApiClientError && error.code === "MFA_REQUIRED";
}

/**
 * What to say when the provider came back without a second factor. Owner answer W3-13: a
 * customer whose own Entra does not require MFA is refused, with what their Entra admin must turn
 * on. A provider that cannot prove MFA to skyData at all is Skypoint's to fix.
 */
export function providerDidNotAskMessage(
  mfa: Pick<MeMfa, "workforce" | "issuerCanProve" | "adminNeedsSso"> | undefined,
): string {
  // #1712: an admin on a sign-in that cannot prove a second step reads the security reason and
  // who can move them to single sign-on, not an operator setup sentence.
  if (mfa?.adminNeedsSso) return ADMIN_OWN_SIGN_IN_NEEDS_SSO_MESSAGE;
  if (mfa && !mfa.issuerCanProve) {
    return "Your sign-in provider is not set up to prove a second step to skyData. Ask a Skypoint operator to finish its multi-factor setup.";
  }
  if (mfa?.workforce) {
    return "Your organization's Microsoft Entra ID did not ask for a second step. Ask your Entra administrator to turn on a Conditional Access policy that requires multifactor authentication for skyData.";
  }
  return "Your sign-in provider did not ask for a second step. Ask your administrator to require MFA for skyData.";
}

/**
 * Issue #2006: the second sign-in step could not start (MSAL refused it: another sign-in in
 * progress, a blocked window, no network). The dialog reads `signInErrorView`'s sentence with the
 * code and the reference id, never Microsoft's text.
 */
export function stepUpRefusedMessage(cause: unknown): string {
  return signInErrorText(cause, currentCorrelationId());
}
